import type { UscStateType } from "../redux/store";
import type {
    FullQuickSpecConfigType,
    QuickSpecConfigType,
    SpecCategoryType,
    UscEcoQuickSpecs,
} from "../../shared-logic/utils/uscUtils";
import { uscEcoQuickSpecs, UscQuickSpec } from "../../shared-logic/utils/uscUtils";
import type { UscSpecParamsSettingsType, UscSpecParamsType } from "../../shared-logic/utils/uscSpecUtils";
import {
    amendQuickSpecConfigs,
    formatFullQuickSpecs,
    formatQuickSpecs,
    getUscLegalSpecConfigs,
    mapConsumptionSpecsToLegalSpecs,
} from "../../shared-logic/utils/uscSpecUtils";
import type { DisclaimerType } from "../../../common-deprecated/types/CommonTypes";
import { DisclaimerLocation } from "../../../common-deprecated/types/CommonTypes";
import type { AEMSpecCategoryType, VehicleType } from "../../shared-logic/types/UscCommonTypes";
import { UscDisclaimerType } from "../../shared-logic/types/UscCommonTypes";
import type { WeightedSpecsLabelType } from "../../shared-logic/utils/uscEcoUtils";
import {
    ecoLabelIconMap,
    UscEcoValueMeasureSystem,
    uscNedcWltpDisclaimersLabelSelector,
} from "../../shared-logic/utils/uscEcoUtils";
import type { UscLabelStateType } from "./labels";
import { getUscLabel } from "./labels";
import { DfFuelTypeId } from "../../shared-logic/utils/dfConstants";
import type { FormattedFullQuickSpecsType, FormattedQuickSpecsType } from "./types";
import { SpecSection } from "./constants";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";

/**
 * Returns a url to the correct ecolabel image
 */
export const getEcoLabelSrc = (commonSettings: CommonSettingsType, ecoCode: string): string =>
    `${commonSettings.resourcePath}/toyota/img/car-filter/${
        ecoLabelIconMap[commonSettings.country]?.find((item) => item.id === ecoCode)?.icon || ""
    }`;

export const uscWeightedSpecsLabelSelector = (state: UscLabelStateType): WeightedSpecsLabelType => ({
    [UscQuickSpec.CombinedCo2]: getUscLabel(state, "uscPdpWeightedCombinedCo2"),
    [UscQuickSpec.Co2NedcCombined]: getUscLabel(state, "uscPdpWeightedCo2NedcCombined"),
    [UscQuickSpec.Co2WltpCombined]: getUscLabel(state, "uscPdpWeightedCo2WltpCombined"),
    [UscQuickSpec.FcNedcCombined]: getUscLabel(state, "uscPdpWeightedFcNedcCombined"),
    [UscQuickSpec.FcWltpCombined]: getUscLabel(state, "uscPdpWeightedFcWltpCombined"),
    [UscQuickSpec.FcCsCombined]: getUscLabel(state, "uscPdpWeightedFcCsCombined"),
    [UscQuickSpec.EcCombined]: getUscLabel(state, "uscPdpWeightedEcCombined"),
    [UscQuickSpec.EfficiencyClassText]: getUscLabel(state, "uscPdpWeightedEfficiencyClassText"),
    [UscQuickSpec.EcolabelEcCombined]: getUscLabel(state, "uscPdpWeightedEcolabelEcCombined"),
});

export const uscSpecsParamsSettingsSelector = (state: UscStateType): UscSpecParamsSettingsType => {
    const { uscSettings } = state;
    return {
        disableMonthInRegistrationSpec: uscSettings.disableMonthInRegistrationSpec,
        showZeroCo2SpecValues: uscSettings.showZeroCo2SpecValues,
        hideCombinedPowerOutput: uscSettings.hideCombinedPowerOutput,
    };
};

/**
 * Selector returning the usc specific spec params.
 * @param state - UscStateType
 */
export const uscSpecsParamsSelector = (state: UscStateType): UscSpecParamsType => {
    const { commonSettings } = state;
    return {
        commonSettings,
        settings: uscSpecsParamsSettingsSelector(state),
        labels: {
            nedcWltpDisclaimerLabels: uscNedcWltpDisclaimersLabelSelector(state),
            quickSpecLabels: {
                yesLabel: commonSettings.labels.uscSpecLabelYes,
                noLabel: commonSettings.labels.uscSpecLabelNo,
                kmLimitLabel: commonSettings.labels.uscManufacturerWarrantyKmLimit,
                validUntilLabel: commonSettings.labels.uscManufacturerWarrantyValidUntil,
                expiredLabel: commonSettings.labels.uscManufacturerWarrantyExpired,
                noneLabel: commonSettings.labels.uscManufacturerWarrantyNone,
                unspecifiedLabel: commonSettings.labels.uscManufacturerWarrantyUnspecified,
            },
            weightedSpecsLabels: uscWeightedSpecsLabelSelector(state),
        },
    };
};

/**
 * Checks if the given specification section contains the required values to be displayed,
 * to prevent empty accordions from being rendered.
 * The consumption section is intentionally excluded from this check, as stricter conditions
 * are defined in the consumption section component.
 */
export const showSpecSection = (section: SpecSection, product?: VehicleType["product"]): boolean => {
    if (!product) return false;

    switch (section) {
        case SpecSection.Dimensions:
            return !!product.doors || !!product.seats || !!product.bootCapacity;

        case SpecSection.Engine:
            return (
                !!product.engine.cylinders ||
                !!product.engine.displacement ||
                !!product.engine.powerOutputHorsepower ||
                !!product.engine.fiscalPower
            );

        case SpecSection.Performance:
            return !!product.performance?.maxSpeed?.value || !!product.performance?.acceleration?.value;

        case SpecSection.Transmission:
            return !!product.transmission.gearbox || !!product.transmission.name;

        case SpecSection.TyreInfo:
            return (
                !!product.tyreSpecs?.tyres ||
                !!product.tyreSpecs?.minimumEfficiencyClass ||
                !!product.tyreSpecs?.minimumEfficiencyClass ||
                !!product.tyreSpecs?.minimumWetGripClass ||
                !!product.tyreSpecs?.minimumNoise
            );

        default:
            return true;
    }
};

type FormattedSpecCategoriesType = {
    [key in SpecSection]: {
        title: string;
        specs: FormattedQuickSpecsType;
    };
};

/**
 * Reduces an array of AEM spec categories into an object where
 * the keys are the category types and the values are the formatted specs.
 * This makes it easy to access the formatted specs of a category by its type.
 * @param specCategories - An array of AEM spec categories.
 * @returns An object of formatted spec categories.
 */
export const formatSpecCategories = (specCategories: AEMSpecCategoryType[]): FormattedSpecCategoriesType =>
    specCategories.reduce((acc, category) => {
        acc[category.type] = {
            title: category.title,
            specs: formatQuickSpecs(category.specs),
        };
        return acc;
    }, {} as FormattedSpecCategoriesType);

/**
 * Selector used to get formatted quickSpecs for the top/extra quickSpecs sections,
 * or specCategories for the specCategories section (flattened to a single array).
 */
export const uscQuickSpecSelector =
    (type: "quickSpecs" | "specCategories" | "all") =>
    (state: UscStateType): FullQuickSpecConfigType[] => {
        const { dealer, vehicle } = state;
        const specParams = uscSpecsParamsSelector(state);

        const { quickSpecsConfigs, specCategories } = state.uscSettings;

        switch (type) {
            case "quickSpecs":
                return amendQuickSpecConfigs(quickSpecsConfigs, specParams, vehicle?.result, dealer?.dealer);
            case "specCategories":
                const collectedSpecs = specCategories.reduce(
                    (acc: QuickSpecConfigType[], specCategory: SpecCategoryType) => {
                        if (specCategory.specs) {
                            acc.push(...specCategory.specs);
                        }

                        return acc;
                    },
                    [],
                );

                return amendQuickSpecConfigs(collectedSpecs, specParams, vehicle?.result, dealer?.dealer);
            case "all":
                const allSpecCategoriesSpecs = specCategories.reduce(
                    (acc: QuickSpecConfigType[], specCategory: SpecCategoryType) => {
                        if (specCategory.specs) {
                            acc.push(...specCategory.specs);
                        }

                        return acc;
                    },
                    [],
                );

                return amendQuickSpecConfigs(
                    [...quickSpecsConfigs, ...allSpecCategoriesSpecs],
                    specParams,
                    vehicle?.result,
                    dealer?.dealer,
                );
        }

        return [];
    };

export const uscSpecDisclaimerSelector =
    (type: "quickSpecs" | "specCategories" | "all") =>
    (state: UscStateType): DisclaimerType<UscDisclaimerType>[] => {
        const nedcWltpDisclaimerLabels = uscNedcWltpDisclaimersLabelSelector(state);

        const specs = uscQuickSpecSelector(type)(state);

        const disclaimers = [];

        specs.forEach((spec) => {
            // Add spec tooltip disclaimers.
            // Don't add spec tooltip disclaimers if the reference is nedc/wltp, we add a separate wltp/nedc disclaimer below.
            if (
                spec.tooltip &&
                ![UscEcoValueMeasureSystem.Nedc, UscEcoValueMeasureSystem.Wltp].includes(
                    spec.tooltipReference as UscEcoValueMeasureSystem,
                )
            ) {
                disclaimers.push({
                    type: UscDisclaimerType.Spec,
                    reference: spec.type,
                    value: spec.tooltip,
                    location: DisclaimerLocation.InfoIcon,
                });
            }
        });

        // Check if there are any ecoQuickSpecs in the list of generated specs.
        // Only then should the wltp/nedc disclaimers be added.
        if (specs.some((spec) => uscEcoQuickSpecs.includes(spec.type as UscEcoQuickSpecs))) {
            if (nedcWltpDisclaimerLabels.wltpDisclaimer) {
                disclaimers.push({
                    type: UscDisclaimerType.Spec,
                    reference: UscEcoValueMeasureSystem.Wltp,
                    value: nedcWltpDisclaimerLabels.wltpDisclaimer,
                    location: DisclaimerLocation.InfoIcon,
                });
            }

            if (nedcWltpDisclaimerLabels.nedcDisclaimer) {
                disclaimers.push({
                    type: UscDisclaimerType.Spec,
                    reference: UscEcoValueMeasureSystem.Nedc,
                    value: nedcWltpDisclaimerLabels.nedcDisclaimer,
                    location: DisclaimerLocation.InfoIcon,
                });
            }
        }

        return disclaimers;
    };

export const ecolabelFuelTypeValuesSelector = (state: UscStateType): { fuelType?: string; otherFuelType?: string } => {
    const { vehicle } = state;

    if (!vehicle.result) return {};

    const { engine } = vehicle.result.product;

    switch (engine.marketingFuelType.code) {
        case DfFuelTypeId.Petrol:
        case DfFuelTypeId.Diesel:
            return {
                fuelType: engine.marketingFuelType.description,
                otherFuelType: getUscLabel(state, "uscPdpEcolabelNotApplicable"),
            };
        // For PHEV & HEV marketingFuelType is "Plug-in Hybrid"/"Hybrid", while the acutal
        // fuelType is either "Petrol" or "Diesel", contained in the fuelType property.
        case DfFuelTypeId.Hybrid:
            return {
                fuelType: engine.fuelType.description,
                otherFuelType: getUscLabel(state, "uscPdpEcolabelNotApplicable"),
            };
        case DfFuelTypeId.PluginHybrid:
            return {
                fuelType: engine.fuelType.description,
                otherFuelType: getUscLabel(state, "uscPdpEcolabelElectric"),
            };
        case DfFuelTypeId.Electric:
            return {
                fuelType: getUscLabel(state, "uscPdpEcolabelNotApplicable"),
                otherFuelType: getUscLabel(state, "uscPdpEcolabelElectric"),
            };
        case DfFuelTypeId.Hydrogen:
            return {
                fuelType: getUscLabel(state, "uscPdpEcolabelNotApplicable"),
                otherFuelType: getUscLabel(state, "uscPdpEcolabelHydrogen"),
            };
    }

    return {};
};

// Specifically used to retrieve the specs we render in the TDG Ecolabel (UC-541)
export const legalSectionSpecsSelector = (state: UscStateType): FormattedFullQuickSpecsType => {
    const { vehicle, dealer, uscSettings } = state;
    const specParams = uscSpecsParamsSelector(state);

    // First, retrieve all consumption specs - we've configured that category to contain _all_ specs required for the ecolabel
    const consumptionSpecs = uscSettings.specCategories.find(
        (category) => category.type === SpecSection.Consumption,
    )?.specs;

    // Map them to the legal spec config type
    const legalSpecs = mapConsumptionSpecsToLegalSpecs(consumptionSpecs);

    if (!legalSpecs) return {};

    // Retrieve the specifications specific to the vehicle's fuel type
    const fuelTypeSpecificSpecs = getUscLegalSpecConfigs(
        legalSpecs,
        vehicle.result?.product.engine.marketingFuelType.code,
    );

    // Amend the legal specs with the necessary data
    const amendedSpecs = amendQuickSpecConfigs(fuelTypeSpecificSpecs, specParams, vehicle?.result, dealer?.dealer);

    // Finally, map these to an object where the keys are the spec type making it easier to render in the component
    return formatFullQuickSpecs(amendedSpecs);
};
